/**
 * @fileoverview Hook to track ad platform sync status after OAuth connection.
 * @module features/settings/hooks/useAdPlatformSyncStatus
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import {
  getIntegrationsStatus,
  INTEGRATIONS_STATUS_QUERY_KEY,
} from '../api/integrationStatusService'
import type { IntegrationProvider } from '../schemas/integrationStatusSchema'

const LOCAL_STORAGE_SYNC_EVENT = 'abyssblock_local_storage_sync'

/** Storage key prefix for connection timestamps */
const SYNC_STARTED_KEY_PREFIX = 'abyssblock_ad_sync_started_'

/** How long to poll aggressively after connection (3 minutes) */
const AGGRESSIVE_POLL_DURATION_MS = 3 * 60 * 1000

/** Aggressive polling interval (10 seconds) */
const AGGRESSIVE_POLL_INTERVAL_MS = 10 * 1000

/** Normal polling interval (60 seconds) */
const NORMAL_POLL_INTERVAL_MS = 60 * 1000

interface AdPlatformSyncStatus {
  /** Whether data is currently syncing (connected recently, no sync completed yet) */
  isSyncing: boolean
  /** When the sync started (connection timestamp) */
  syncStartedAt: Date | null
  /** When the last successful sync completed */
  lastSyncedAt: Date | null
  /** Number of records from last sync */
  lastSyncRecordsCount: number | null
  /** Mark that a connection just completed - starts aggressive polling */
  markConnectionComplete: () => void
  /** Clear the syncing state */
  clearSyncingState: () => void
}

/**
 * Gets the storage key for a provider and org
 */
function getStorageKey(provider: IntegrationProvider, orgId: string): string {
  return `${SYNC_STARTED_KEY_PREFIX}${provider}_${orgId}`
}

/**
 * Hook to track ad platform sync status after OAuth connection.
 *
 * After connection, polls more aggressively (every 10s) for 3 minutes
 * to detect when sync completes. Returns `isSyncing: true` until
 * `lastSyncedAt` is newer than the connection timestamp.
 *
 * @param provider - The integration provider ('FACEBOOK' or 'GOOGLE_ADS')
 * @returns Sync status and control functions
 *
 * @example
 * ```tsx
 * const { isSyncing, markConnectionComplete } = useAdPlatformSyncStatus('GOOGLE_ADS')
 *
 * // After OAuth completes:
 * await completeGoogleAdsOAuth(account)
 * markConnectionComplete()
 *
 * // In UI:
 * {isSyncing && <span>Syncing your ad data...</span>}
 * ```
 */
export function useAdPlatformSyncStatus(
  provider: 'FACEBOOK' | 'GOOGLE_ADS' | 'BASELINKER'
): AdPlatformSyncStatus {
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()

  const storageKey = activeOrgId ? getStorageKey(provider, activeOrgId) : null

  const subscribeToLocalStorage = useCallback((onStoreChange: () => void) => {
    if (typeof window === 'undefined') return () => {}

    const handler = (): void => onStoreChange()
    window.addEventListener('storage', handler)
    window.addEventListener(LOCAL_STORAGE_SYNC_EVENT, handler)
    return () => {
      window.removeEventListener('storage', handler)
      window.removeEventListener(LOCAL_STORAGE_SYNC_EVENT, handler)
    }
  }, [])

  const syncStartedAtIso = useSyncExternalStore(
    subscribeToLocalStorage,
    () => {
      if (typeof window === 'undefined' || !storageKey) return null
      return localStorage.getItem(storageKey)
    },
    () => null
  )

  const syncStartedAt = useMemo((): Date | null => {
    if (!syncStartedAtIso) return null
    const date = new Date(syncStartedAtIso)
    return Number.isNaN(date.getTime()) ? null : date
  }, [syncStartedAtIso])

  // Poll aggressively whenever we're tracking an in-progress sync.
  // The timeout effect below clears `syncStartedAt` after AGGRESSIVE_POLL_DURATION_MS.
  const shouldPollAggressively = syncStartedAt !== null

  // Query for integration status with dynamic polling
  const { data: statuses } = useQuery({
    queryKey: activeOrgId
      ? ['org', activeOrgId, ...INTEGRATIONS_STATUS_QUERY_KEY]
      : INTEGRATIONS_STATUS_QUERY_KEY,
    queryFn: () => {
      if (!activeOrgId) throw new Error('No active organization')
      return getIntegrationsStatus(activeOrgId)
    },
    enabled: !!activeOrgId,
    staleTime: shouldPollAggressively ? 0 : NORMAL_POLL_INTERVAL_MS,
    refetchInterval: shouldPollAggressively
      ? AGGRESSIVE_POLL_INTERVAL_MS
      : NORMAL_POLL_INTERVAL_MS,
  })

  // Find the status for our provider
  const providerStatus = useMemo(() => {
    return statuses?.find((s) => s.provider === provider)
  }, [statuses, provider])

  // Parse lastSyncedAt
  const lastSyncedAt = useMemo(() => {
    if (!providerStatus?.lastSyncedAt) return null
    return new Date(providerStatus.lastSyncedAt)
  }, [providerStatus])

  // Determine if we're syncing: started recently and no sync completed since
  const isSyncing =
    syncStartedAt !== null &&
    (lastSyncedAt === null || lastSyncedAt.getTime() <= syncStartedAt.getTime())

  // Auto-clear syncing state when sync completes and invalidate dashboard data
  useEffect(() => {
    if (
      syncStartedAt &&
      lastSyncedAt &&
      lastSyncedAt.getTime() > syncStartedAt.getTime()
    ) {
      // Sync completed - clear the stored timestamp
      if (storageKey) {
        localStorage.removeItem(storageKey)
        window.dispatchEvent(new Event(LOCAL_STORAGE_SYNC_EVENT))

        // Invalidate dashboard/statistics queries so new ad data is visible
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) && query.queryKey.includes('statistics'),
        })
      }
    }
  }, [syncStartedAt, lastSyncedAt, storageKey, queryClient])

  // Also auto-clear after timeout
  useEffect(() => {
    if (!syncStartedAt || !storageKey) return

    const elapsed = Date.now() - syncStartedAt.getTime()
    if (elapsed >= AGGRESSIVE_POLL_DURATION_MS) {
      // Timeout reached - clear state
      localStorage.removeItem(storageKey)
      window.dispatchEvent(new Event(LOCAL_STORAGE_SYNC_EVENT))
      return
    }

    // Set timeout to clear after remaining time
    const remaining = AGGRESSIVE_POLL_DURATION_MS - elapsed
    const timeoutId = setTimeout(() => {
      localStorage.removeItem(storageKey)
      window.dispatchEvent(new Event(LOCAL_STORAGE_SYNC_EVENT))
    }, remaining)

    return () => clearTimeout(timeoutId)
  }, [syncStartedAt, storageKey])

  // Mark that a connection just completed
  const markConnectionComplete = useCallback(() => {
    const now = new Date()
    if (!storageKey) return
    localStorage.setItem(storageKey, now.toISOString())
    window.dispatchEvent(new Event(LOCAL_STORAGE_SYNC_EVENT))

    // Invalidate queries to trigger immediate refetch
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey.includes('integrations-status'),
    })
  }, [storageKey, queryClient])

  // Clear syncing state manually
  const clearSyncingState = useCallback(() => {
    if (!storageKey) return
    localStorage.removeItem(storageKey)
    window.dispatchEvent(new Event(LOCAL_STORAGE_SYNC_EVENT))
  }, [storageKey])

  return {
    isSyncing,
    syncStartedAt,
    lastSyncedAt,
    lastSyncRecordsCount: providerStatus?.lastSyncRecordsCount ?? null,
    markConnectionComplete,
    clearSyncingState,
  }
}
